import getPieveWorkflowStepConnectionRef, { isPieveWorkflowStepConnectionRefMap } from '@shared/pieve/workflowStepConnectionRef.js';
import resolveWorkflowRunEnvironments from '@/redux/selectors/pieveSelectors/lib/resolveWorkflowRunEnvironments.js';

/*
 * Per-app, the connection each environment binds — the input the workflow rail needs to show a
 * multi-environment workflow as one row per environment instead of one row per credential.
 *
 * Built from the draft definition rather than the per-app status response because the mapping is a
 * property of the STEP: the server's status read collapses every step sharing an app into one row and
 * so cannot carry more than one credential for it. The status of each credential still comes from that
 * response; only the environment→credential assignment comes from here.
 *
 * Keyed by the step's `appId`, else lowercased `appSlug`; the status row carries its slug always and
 * its id whenever any step had one, and the rail looks up both, so the two sides join either way. When
 * two steps share an app with different maps the first wins, matching `collectStepApps` — a per-app row
 * cannot state two answers, and saying the first is better than saying none.
 */
const toAppKey = ({ step }) => {
  const appId = String(step?.appId || step?.app?.id || '').trim();
  const appSlug = String(step?.appSlug || step?.appRef || step?.app?.slug || '')
    .trim()
    .toLowerCase();

  return appId || appSlug;
};

const listSteps = ({ steps, out = [] }) => {
  if (!Array.isArray(steps)) {
    return out;
  }

  steps.forEach((step) => {
    out.push(step);
    listSteps({ steps: step?.children, out });

    const branches = step?.branchesByKey || step?.branches;

    if (branches && typeof branches === 'object' && !Array.isArray(branches)) {
      Object.values(branches).forEach((branchSteps) => listSteps({ steps: branchSteps, out }));
    }
  });

  return out;
};

const buildStepEnvironmentConnectionRefs = ({ definition, environments }) => {
  const steps = listSteps({ steps: definition?.steps });
  const mappedSteps = steps.filter((step) => isPieveWorkflowStepConnectionRefMap(step?.connectionRef));

  if (mappedSteps.length === 0) {
    return {};
  }

  const environmentsInScope = resolveWorkflowRunEnvironments({ steps: definition?.steps, environments });
  const byAppKey = {};

  mappedSteps.forEach((step) => {
    const appKey = toAppKey({ step });

    if (!appKey || byAppKey[appKey]) {
      return;
    }

    byAppKey[appKey] = environmentsInScope.map((environment) => ({
      environmentId: environment.id,
      environmentName: environment.name,
      connectionRef: getPieveWorkflowStepConnectionRef({ step, environmentId: environment.id })
    }));
  });

  return byAppKey;
};

export default buildStepEnvironmentConnectionRefs;
